// Discovery Engine Phase 3A — Builder provenance banner.
//
// When a strategy graph originated from Discovery, the Builder must visibly say so
// and keep a link back to the source run/candidate. This reads the provenance the
// Builder-owned compiler stamped onto the strategy_config block — it is purely
// presentational (no Discovery runtime import), so it costs nothing for ordinary
// strategies (it renders null when there is no Discovery provenance).

import type { Block } from '../../types';

interface DiscoveryProvenance {
  discoveryCandidateId: string;
  discoveryRunId: string;
  hypothesisCardId: string;
  failureAxes: string[];
  validationPlan: string;
  dataSnapshotId: string;
  discoveryEngineVersion: string;
  evaluatedPoint: Record<string, number>;
}

function readProvenance(blocks: Block[]): DiscoveryProvenance | null {
  const cfg = blocks.find((b) => String(b.type) === 'strategy_config');
  const p = (cfg?.data as { discoveryProvenance?: DiscoveryProvenance } | undefined)?.discoveryProvenance;
  if (!p || !p.discoveryRunId || !p.discoveryCandidateId) return null;
  return p;
}

export default function DiscoveryProvenanceBanner({ blocks, onOpenRun }: { blocks: Block[]; onOpenRun?: (runId: string) => void }) {
  const p = readProvenance(blocks);
  if (!p) return null;
  return (
    <div
      role="note"
      aria-label="Strategy origin: Discovery"
      data-testid="discovery-provenance-banner"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 mb-2 text-xs bg-indigo-50 border border-indigo-200 rounded-md text-indigo-900"
    >
      <span className="font-semibold">From Discovery</span>
      <span className="font-mono" data-testid="provenance-run">run {p.discoveryRunId}</span>
      <span className="font-mono" data-testid="provenance-candidate">candidate {p.discoveryCandidateId}</span>
      <span className="text-indigo-700">hypothesis {p.hypothesisCardId}</span>
      <span className="text-indigo-700">snapshot {p.dataSnapshotId}</span>
      <span className="text-indigo-700">Validation plan: {p.validationPlan}</span>
      <span className="text-indigo-600">point: {Object.entries(p.evaluatedPoint).map(([k, v]) => `${k}=${v}`).join(' · ')}</span>
      <span className="text-indigo-500">This is a research candidate — not validated or approved to trade.</span>
      {onOpenRun && (
        <button
          type="button"
          onClick={() => onOpenRun(p.discoveryRunId)}
          className="ml-auto underline hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
          data-testid="provenance-back-link"
        >
          Open source run →
        </button>
      )}
    </div>
  );
}
